import { createFileRoute, Link } from '@tanstack/react-router'
import { Button } from '@/components/ui/button'
import { PlanFinePrint, StartFreeButton } from '@/components/marketing/free-plan-cta'
import { article, jsonLdScript } from '@/lib/schema'

// Source of truth: the supported lists live in uptrack-server (crates/notifications/src/telnyx.rs,
// DEFAULT_SENDER_ID_PREFIXES and DEFAULT_VOICE_PREFIXES) and the plan limits in
// crates/notifications/src/quota.rs. Keep this page in step with them.

// We text and call every country except these. SMS to the United States and Canada is off until the
// toll-free number is verified (TELNYX_US_SMS_ENABLED); calls there work. Keep in step with
// DEFAULT_SMS_DENY_PREFIXES (used for both SMS and voice).
const NOT_REACHED: string[] = [
  'Belarus', 'China', 'Cuba', 'Iran', 'Kazakhstan', 'North Korea', 'Russia', 'Syria',
]

const PLANS: { plan: string; sms: string; intl: string; calls: string; intlCalls: string; where: string }[] = [
  { plan: 'Free', sms: 'Not included', intl: 'Not included', calls: 'Not included', intlCalls: 'Not included', where: 'None' },
  { plan: 'Pro', sms: '20', intl: '5', calls: '5', intlCalls: '2', where: 'Lithuania, United Arab Emirates, Vietnam for SMS; also US and Canada for calls' },
  { plan: 'Team', sms: '50', intl: '10', calls: '10', intlCalls: '3', where: 'Every supported country' },
  { plan: 'Business', sms: '150', intl: '30', calls: '30', intlCalls: '10', where: 'Every supported country' },
]

const FAQ: { q: string; a: string }[] = [
  {
    q: 'Which countries can receive SMS alerts?',
    a: 'Almost every country: more than 200 countries and territories, except the United States and Canada. SMS to the United States and Canada is coming soon, because US phone companies block texts until the sending number is verified. Phone calls, push, email, Slack and Telegram already work there. Everywhere else the text comes from the sender name Uptrack. In some countries the phone company may replace the sender name with a generic one, but the message still arrives. Pro covers Lithuania, the United Arab Emirates and Vietnam. Team and Business cover every country we send to.',
  },
  {
    q: 'Which countries can receive phone-call alerts?',
    a: 'Almost every country: close to 200 countries and territories. Calls ring from a US number, and a few phone companies block or label calls that come from abroad, so a call can fail in a country we have not tested. Vietnam is tested and works. Pro covers the United States, Canada, Lithuania, the United Arab Emirates and Vietnam. Team and Business cover every country we call. We do not call the Caribbean countries that share the US country code, nor a few small-island ranges where calls cost far more and fraud is common. Ask us if you need one.',
  },
  {
    q: 'Why can’t I add a number from my country?',
    a: 'Either we do not send there, or your plan does not include that country. The error message says which. We do not send SMS or place calls to Belarus, China, Cuba, Iran, Kazakhstan, North Korea or Russia, or to satellite and premium-rate numbers. If your country is on that list, send alerts by email, Slack, Discord, Telegram or push instead.',
  },
  {
    q: 'What does it cost?',
    a: 'Nothing extra. SMS and phone-call alerts are included in Pro, Team and Business, with the monthly limits in the table above. There is no per-message charge and no credits to buy. They are not part of the Free plan.',
  },
  {
    q: 'Why does an SMS outside the United States and Canada have its own limit?',
    a: 'It costs us much more to deliver. An SMS to Vietnam costs us about 17 cents. Every SMS counts toward the monthly total, and an international SMS also counts toward the smaller international limit, so one busy night cannot use up the plan.',
  },
  {
    q: 'What happens when I reach the monthly limit?',
    a: 'SMS and calls pause until the first of the next month (UTC). Email, Slack, Discord, Telegram and push alerts keep working. Sending a test message from the dashboard also counts toward the limit.',
  },
  {
    q: 'What does the phone call say, and how do I acknowledge it?',
    a: 'The call rings only for a real outage, never for a recovery or a slow response. It says that the monitor is down, twice, then waits for a key press. Press 1 to acknowledge the incident, which stops further escalation.',
  },
  {
    q: 'How do I stop receiving SMS alerts?',
    a: 'Reply STOP to any alert. Reply START to receive them again. You can also delete the channel in the dashboard.',
  },
  {
    q: 'Can you add my country?',
    a: 'Probably, in the order our customers need it. Email support@uptrack.app with the country and the plan you use. Countries with paying customers come first.',
  },
]

export const Route = createFileRoute('/_site/guides/sms-and-call-alerts-faq')({
  component: SmsCallAlertsFaqPage,
  head: () => ({
    meta: [
      { title: 'SMS and Phone Call Alerts: Countries and Limits | Uptrack' },
      {
        name: 'description',
        content:
          'Which countries get SMS and phone-call uptime alerts from Uptrack, what each plan includes, and why some countries are not supported yet.',
      },
      { property: 'og:title', content: 'SMS and phone-call alerts: supported countries, limits and cost' },
      {
        property: 'og:description',
        content: 'The countries we can reach by SMS and by phone call, the monthly limits per plan, and what it costs.',
      },
    ],
    links: [{ rel: 'canonical', href: 'https://uptrack.app/guides/sms-and-call-alerts-faq' }],
    scripts: [
      jsonLdScript(
        article({
          headline: 'SMS and Phone Call Alerts: Countries and Limits',
          description: 'Which countries get SMS and phone-call uptime alerts from Uptrack, what each plan includes, and why some countries are not supported yet.',
          url: 'https://uptrack.app/guides/sms-and-call-alerts-faq',
          datePublished: '2026-10-23',
        }),
      ),
      {
        type: 'application/ld+json',
        children: JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: FAQ.map(({ q, a }) => ({
            '@type': 'Question',
            name: q,
            acceptedAnswer: { '@type': 'Answer', text: a },
          })),
        }),
      },
    ],
  }),
})

const P = 'mb-4 text-lg leading-[1.7] text-body'
const H2 = 'font-sans mb-4 text-2xl font-semibold'

function SmsCallAlertsFaqPage() {
  return (
    <article className="article-prose container mx-auto max-w-2xl px-4 py-16">
      <p className="font-sans mb-3 text-sm font-medium uppercase tracking-wide text-primary">Alerts</p>
      <h1 className="font-sans mb-4 text-4xl font-semibold">SMS and phone-call alerts: countries, limits and cost</h1>
      <p className="mb-8 text-lg leading-[1.7] text-muted-foreground">
        A phone call wakes you when a chat message will not. Here is where we can reach you, what each plan
        includes, and what happens when something is not supported yet.
      </p>

      <section className="mb-10">
        <h2 className={H2}>What each plan includes</h2>
        <p className={P}>
          SMS and calls are included in Pro, Team and Business. The limits are per month and reset on the first
          (UTC). There is nothing to buy on top.
        </p>
        <div className="mb-4 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b">
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Plan</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">SMS per month</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">of which outside US and Canada</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">Calls per month</th>
                <th className="pb-3 pr-4 text-left font-medium text-muted-foreground">of which outside US and Canada</th>
                <th className="pb-3 text-left font-medium text-muted-foreground">Countries</th>
              </tr>
            </thead>
            <tbody>
              {PLANS.map((row) => (
                <tr key={row.plan} className="border-b border-border">
                  <td className="py-3 pr-4 font-medium text-foreground">{row.plan}</td>
                  <td className="py-3 pr-4 text-body">{row.sms}</td>
                  <td className="py-3 pr-4 text-body">{row.intl}</td>
                  <td className="py-3 pr-4 text-body">{row.calls}</td>
                  <td className="py-3 pr-4 text-body">{row.intlCalls}</td>
                  <td className="py-3 text-body">{row.where}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="text-sm text-muted-foreground">
          Every SMS and call counts toward the monthly total. One outside the United States and Canada also counts
          toward the smaller international limit, because it costs more to deliver.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Countries we can send an SMS to</h2>
        <p className={P}>
          More than 200 countries and territories. We send to every country except these: {NOT_REACHED.join(', ')},
          and satellite or premium-rate numbers. SMS to the United States and Canada is coming soon: US phone companies
          block texts from an unverified number, so we keep it off rather than let alerts fail silently. Phone calls to
          the United States and Canada work today.
        </p>
        <p className={P}>
          Delivery depends on the local phone companies. If a country wants a registered sender name, the
          message can arrive under a generic name instead of Uptrack.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Countries we can call</h2>
        <p className={P}>
          Close to 200 countries and territories: the SMS list without the Caribbean countries that share the US
          country code and a few small-island ranges where calls cost far more and fraud is common. We also do not call{' '}
          {NOT_REACHED.join(', ')} or satellite and premium-rate numbers. Calls come from a US number. If a phone
          company blocks calls from abroad, the call fails and the SMS and your other channels still go out.
        </p>
      </section>

      <section className="mb-10">
        <h2 className={H2}>How to add a number</h2>
        <ol className="mb-4 list-decimal space-y-2 pl-6">
          <li className="text-lg leading-[1.7] text-body">Open Alerts in the dashboard and add a channel.</li>
          <li className="text-lg leading-[1.7] text-body">Choose SMS or Phone call.</li>
          <li className="text-lg leading-[1.7] text-body">
            Enter the number with the country code, for example +12025550123.
          </li>
          <li className="text-lg leading-[1.7] text-body">
            Send a test. It counts toward your monthly limit, so one test is enough.
          </li>
        </ol>
      </section>

      <section className="mb-10">
        <h2 className={H2}>Questions</h2>
        {FAQ.map(({ q, a }) => (
          <div key={q} className="mb-6">
            <h3 className="font-sans mb-2 text-lg font-semibold">{q}</h3>
            <p className="text-lg leading-[1.7] text-body">{a}</p>
          </div>
        ))}
      </section>

      <div className="font-sans mt-12 rounded-lg border border-border bg-muted p-6 text-center">
        <h2 className="mb-2 text-2xl font-semibold">Get a call when it matters</h2>
        <p className="mx-auto mb-6 max-w-lg text-base text-body">
          SMS and phone-call alerts come with the paid plans. The free plan sends email and one chat channel.
        </p>
        <div className="mb-4 flex flex-wrap items-center justify-center gap-3">
          <StartFreeButton />
          <Button size="lg" variant="outline" className="h-11 px-5 text-[15px]" asChild>
            <Link to="/pricing">See plans</Link>
          </Button>
        </div>
        <PlanFinePrint />
      </div>
    </article>
  )
}
